import * as Ably from "ably";
import type { Dispatch, Middleware, UnknownAction } from "@reduxjs/toolkit";
import { getAblyRealtimeClient, replaceAblyRealtimeClient } from "_shared/_stores/realtime/ably-client";
import {
  ConnectionHealthRegistration,
  ConnectionHealthStatus,
  registerConnection,
  updateConnectionStatus,
} from "_shared/_stores/realtime/connection-health-redux-slice";
import { isFunction } from "lodash";
import Session, { SESSION_AUTH_TOKEN_CHANGED_EVENT, SESSION_ESTABLISHED_EVENT } from "users/__session/session";

export type AblyPublish = (name: string, data: unknown) => Promise<Ably.PublishResult>;
type AblyConnectionStateChange = { current: Ably.ConnectionState; reason?: unknown };
type AblyChannelStateChange = { current: Ably.ChannelState; reason?: unknown; resumed?: boolean };

export interface AblyRealtimeChannel {
  name: string;
  publish(name: string, data: unknown): Promise<Ably.PublishResult>;
  subscribe(name: string, listener: (message: Ably.Message) => void | Promise<void>): Promise<unknown> | void;
  on(listener: (stateChange: AblyChannelStateChange) => void): void;
  // Optional so lightweight test doubles need not implement them. Real Ably channels always do,
  // and both are needed to let go of a channel that belonged to a previous session identity.
  unsubscribe?(name?: string, listener?: (message: Ably.Message) => void | Promise<void>): void;
  detach?(): Promise<void> | void;
}

export interface AblyRealtimeClient {
  auth: { authorize(): Promise<unknown> };
  channels: { get(name: string): AblyRealtimeChannel };
  close(): void;
  connect(): void;
  connection: {
    on(listener: (stateChange: AblyConnectionStateChange) => void): void;
    once(state: "connected" | "closed"): Promise<void>;
    state: Ably.ConnectionState;
  };
}

export type RealtimeThunk<S = unknown, D extends Dispatch = Dispatch<UnknownAction>, R = void | Promise<void>> = (
  dispatch: D,
  getState: () => S,
  publish: AblyPublish,
) => R;

export type EventCallback<S = unknown, D extends Dispatch = Dispatch<UnknownAction>> = (
  payload: unknown,
  message: Ably.Message,
) => RealtimeThunk<S, D>;

export interface WithCallbacks<S = unknown, D extends Dispatch = Dispatch<UnknownAction>> {
  add(name: string, callback: EventCallback<S, D>): WithCallbacks<S, D>;
  callbackArray: Array<{ name: string; callback: EventCallback<S, D> }>;
}

export const withCallbacks = <S = unknown, D extends Dispatch = Dispatch<UnknownAction>>(): WithCallbacks<S, D> => {
  const callbackArray = new Array<{ name: string; callback: EventCallback<S, D> }>();
  return {
    add(name: string, callback: EventCallback<S, D>) {
      callbackArray.push({ name, callback });
      return this;
    },
    callbackArray,
  };
};

export interface MiddlewareConfig<S = unknown, D extends Dispatch = Dispatch<UnknownAction>> {
  callbacks: WithCallbacks<S, D>;
  channelName: string | ((session: Session) => string | undefined);
  client?: AblyRealtimeClient;
  createReplacementClient?: () => AblyRealtimeClient;
  connectionHealth?: ConnectionHealthRegistration;
  onStart?: () => void;
  onContinuityLost?: (dispatch: D, getState: () => S) => void;
  shouldConnectionStartImmediately?: boolean;
}

type StatusReporter = (status: ConnectionHealthStatus, error?: unknown) => void;
type LifecycleRegistration = {
  createReplacementClient?: () => AblyRealtimeClient;
  onStart?: () => void;
  reportStatus?: StatusReporter;
  /** Drops the channel this middleware holds so the next start resubscribes from scratch. */
  releaseChannel?: () => void;
  /** Points this middleware at a new shared client after an authenticated principal change. */
  replaceClient?: (client: AblyRealtimeClient) => void;
};

/**
 * Ably binds a connection's `clientId` at connect time, and the Attorney Hub Ably token names the
 * signed-in user as that `clientId`. A session change that swaps the user — logging in as another
 * user from the super-user dashboard, or accepting an invitation from a tab that already had a
 * session — therefore cannot be applied by re-authorizing the live connection: the new token's
 * identity and capabilities do not match the open connection, so it fails and the app reports
 * "Connection to Attorney Hub lost...". Such a change is applied by closing the connection and
 * opening a new one.
 */
type SessionIdentity = { token: string; userId?: string };

const readSessionIdentity = (session: Session): SessionIdentity | undefined => {
  const token = session.authToken?.value;
  if (!token) return undefined;
  return { token, userId: session.user?.id?.value };
};

const connectLocks = new WeakMap<AblyRealtimeClient, Promise<void>>();
const authChangeLocks = new WeakMap<AblyRealtimeClient, Promise<void>>();
const lastStartedIdentityByClient = new WeakMap<AblyRealtimeClient, SessionIdentity>();
const lifecycleInstalled = new WeakSet<AblyRealtimeClient>();
const lifecycleRegistrations = new WeakMap<AblyRealtimeClient, Set<LifecycleRegistration>>();
type LifecycleDocumentHandlers = {
  applySessionChange: EventListener;
  handleLoggedOut: EventListener;
};
const lifecycleDocumentHandlers = new WeakMap<AblyRealtimeClient, LifecycleDocumentHandlers>();
type ChannelLifecycleRegistration = {
  reportStatus?: StatusReporter;
  onContinuityLost?: () => void;
};
const channelLifecycleInstalled = new WeakSet<AblyRealtimeChannel>();
const channelLifecycleRegistrations = new WeakMap<AblyRealtimeChannel, Set<ChannelLifecycleRegistration>>();

const isRealtimeThunk = (action: unknown): action is RealtimeThunk =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  typeof action === "function" && (action as any).__realtimeThunk === true;

const toErrorText = (error: unknown): string | null => {
  if (!error) return null;
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;

  if (typeof error === "object" && "message" in error && typeof (error as { message?: unknown }).message === "string") {
    return (error as { message: string }).message;
  }

  try {
    return JSON.stringify(error);
  } catch {
    return "Unknown Ably error";
  }
};

const mapConnectionStateToHealth = (state: Ably.ConnectionState): ConnectionHealthStatus => {
  if (state === "connected") return "connected";
  if (state === "connecting" || state === "initialized") return "connecting";
  if (state === "disconnected") return "reconnecting";
  if (state === "closing" || state === "closed") return "stopped";
  return "disconnected";
};

const mapChannelStateToHealth = (state: Ably.ChannelState): ConnectionHealthStatus => {
  if (state === "attached") return "connected";
  if (state === "attaching" || state === "initialized") return "connecting";
  if (state === "detached" || state === "detaching") return "stopped";
  return "disconnected";
};

const resolveChannelName = (channelName: MiddlewareConfig["channelName"]): string | undefined => {
  if (typeof channelName === "string") return channelName;
  return channelName(Session.loadFromStorage(() => {}));
};

export const connect = async (client: AblyRealtimeClient, onStart?: () => void, reportStatus?: StatusReporter): Promise<void> => {
  const existing = connectLocks.get(client);
  if (existing) return existing;

  const task = (async () => {
    const session = Session.loadFromStorage(() => {});
    const identity = readSessionIdentity(session);

    // Session.loadFromStorage never returns an expired token: it clears the stale credential from
    // storage and hands back a fresh empty session, so expiry arrives here as a missing identity
    // rather than as an expired one. Signing out on expiry is SessionProvider's job, and the
    // loggedOut event its logout() raises reaches handleLoggedOut below.
    if (!identity) {
      reportStatus?.("stopped");
      lastStartedIdentityByClient.delete(client);
      client.close();
      return;
    }

    if (client.connection.state === "connected") {
      reportStatus?.("connected");
      if (!lastStartedIdentityByClient.has(client)) {
        lastStartedIdentityByClient.set(client, identity);
      }
      return;
    }

    if (client.connection.state !== "initialized" && client.connection.state !== "closed" && client.connection.state !== "failed") {
      return;
    }

    reportStatus?.("connecting");
    client.connect();
    try {
      await client.connection.once("connected");
      lastStartedIdentityByClient.set(client, identity);
      reportStatus?.("connected");
      onStart?.();
    } catch (error) {
      reportStatus?.("disconnected", error);
      throw error;
    }
  })().finally(() => {
    connectLocks.delete(client);
  });

  connectLocks.set(client, task);
  return task;
};

const closeAndWait = async (client: AblyRealtimeClient): Promise<void> => {
  client.close();
  if (client.connection.state === "closed" || client.connection.state === "initialized") return;
  await client.connection.once("closed").catch(() => undefined);
};

/**
 * Applies a session change to the realtime connection. This is the one place a login of any kind
 * reaches Ably: every login context establishes its session through
 * {@link Session.initializeFromAuthResponse}, which raises the events this is wired to.
 */
const reconnectForSessionChange = (
  client: AblyRealtimeClient,
  onStart?: () => void,
  reportStatus?: StatusReporter,
): Promise<void> => {
  const existing = authChangeLocks.get(client);
  if (existing) return existing;

  const task = (async () => {
    const activeConnect = connectLocks.get(client);
    if (activeConnect) {
      await activeConnect.catch(() => undefined);
    }

    const session = Session.loadFromStorage(() => {});
    const identity = readSessionIdentity(session);
    const lastStartedIdentity = lastStartedIdentityByClient.get(client);

    if (!identity) {
      lastStartedIdentityByClient.delete(client);
      reportStatus?.("stopped");
      await closeAndWait(client);
      return;
    }

    // The session now belongs to a different user. An Ably Realtime instance keeps the clientId from
    // its first successful authentication for its entire lifetime, even after close(). Tear down the
    // old instance, move every middleware registration to a fresh shared client, and let that client
    // authenticate for the new principal before subscribing to any user-scoped channel.
    if (lastStartedIdentity !== undefined && lastStartedIdentity.userId !== identity.userId) {
      lastStartedIdentityByClient.delete(client);
      releaseLifecycleChannels(client);
      await closeAndWait(client);

      const replacementClient = replaceLifecycleClient(client);
      if (replacementClient) {
        await connect(
          replacementClient,
          () => startLifecycleRegistrations(replacementClient),
          (status, error) => reportLifecycleStatus(replacementClient, status, error),
        );
        return;
      }

      // Explicitly injected lightweight clients in isolated tests may not provide a replacement
      // factory. Refresh their token as a backwards-compatible fallback; production always replaces.
      reportStatus?.("connecting");
      try {
        await client.auth.authorize();
        lastStartedIdentityByClient.set(client, identity);
        reportStatus?.("connected");
        onStart?.();
      } catch (error) {
        reportStatus?.("disconnected", error);
        throw error;
      }
      return;
    }

    if (client.connection.state === "connected" && lastStartedIdentity?.token !== identity.token) {
      reportStatus?.("connecting");
      await client.auth.authorize();
      lastStartedIdentityByClient.set(client, identity);
      reportStatus?.("connected");
      onStart?.();
      return;
    }

    if (client.connection.state !== "connected") {
      await connect(client, onStart, reportStatus);
      return;
    }

    onStart?.();
  })().finally(() => {
    authChangeLocks.delete(client);
  });

  authChangeLocks.set(client, task);
  return task;
};

const reduxAblyMiddleware = <S = unknown, D extends Dispatch = Dispatch<UnknownAction>>(
  config: MiddlewareConfig<S, D>,
): Middleware<object, S, D> => {
  const {
    callbacks,
    channelName,
    connectionHealth,
    onContinuityLost,
    onStart,
    shouldConnectionStartImmediately = true,
  } = config;
  const initialClient = config.client ?? (getAblyRealtimeClient() as unknown as AblyRealtimeClient);
  const createReplacementClient =
    config.createReplacementClient ??
    (config.client ? undefined : () => replaceAblyRealtimeClient() as unknown as AblyRealtimeClient);

  const middleware: Middleware<object, S, D> = (store) => {
    let client = initialClient;
    const reportStatus: StatusReporter | undefined = connectionHealth
      ? (status, error) => {
          store.dispatch(
            updateConnectionStatus({
              ...connectionHealth,
              status,
              lastError: toErrorText(error),
            }) as UnknownAction,
          );
        }
      : undefined;

    let channel: AblyRealtimeChannel | undefined;
    let channelListeners: Array<{ name: string; listener: (message: Ably.Message) => void | Promise<void> }> = [];
    let subscribedChannelName: string | undefined;
    /** Counts how many times this middleware has let go of its channel, so a subscription that
     * belonged to an earlier one can be recognised no matter which channel replaced it. */
    let channelGeneration = 0;
    const channelLifecycleRegistration: ChannelLifecycleRegistration = {
      reportStatus,
      onContinuityLost: onContinuityLost
        ? () => onContinuityLost(store.dispatch, store.getState as () => S)
        : undefined,
    };
    // Lets go of the channel opened for a previous session. Without this the old channel keeps its
    // state listener registered against this connection-health key, so once the new session's token
    // revokes access to it, its failure is what the header's connection monitor reports.
    const releaseChannel = () => {
      if (channel) {
        const releasedChannel = channel;
        releaseChannelLifecycle(releasedChannel, channelLifecycleRegistration);
        try {
          // Only this middleware's own listeners come off, and the channel is only detached once no
          // other middleware is still listening on it. Two domains can share one channel name —
          // messaging and proposal comments both read the session's `messages` channel, and
          // `channels.get` hands them the same channel object — so a blanket unsubscribe would strip
          // a sibling's listeners and a unilateral detach would tear the channel out from under it.
          for (const { name, listener } of channelListeners) {
            releasedChannel.unsubscribe?.(name, listener);
          }
          if (!isChannelStillInUse(releasedChannel)) {
            void Promise.resolve(releasedChannel.detach?.()).catch(() => undefined);
          }
        } catch (error) {
          console.info("Releasing a stale Ably channel failed.", error);
        }
      }
      channel = undefined;
      channelListeners = [];
      subscribedChannelName = undefined;
      channelGeneration += 1;
    };

    const subscribeChannel = () => {
      const resolvedChannelName = resolveChannelName(channelName);
      if (!resolvedChannelName) {
        releaseChannel();
        return undefined;
      }

      if (subscribedChannelName === resolvedChannelName) {
        channel = client.channels.get(resolvedChannelName);
        return channel;
      }

      releaseChannel();
      const subscribedChannel = client.channels.get(resolvedChannelName);
      const subscribedGeneration = channelGeneration;
      channel = subscribedChannel;
      for (const { name, callback } of callbacks.callbackArray) {
        const listener = (message: Ably.Message) => {
          try {
            const thunk = callback(message.data, message);
            void Promise.resolve(thunk(store.dispatch, store.getState as () => S, publish)).catch((error) => {
              console.error(`Ably handler for "${name}" failed:`, error);
            });
          } catch (error) {
            console.error(`Ably callback for "${name}" threw:`, error);
          }
        };
        channelListeners.push({ name, listener });
        const subscription = subscribedChannel.subscribe(name, listener);
        void Promise.resolve(subscription).catch((error) => {
          // A subscription this middleware has already let go of failed by this app's own doing: a
          // session change detaches a channel while Ably is still attaching it, and Ably rejects the
          // superseded attach. That is teardown finishing, not the current session losing realtime,
          // so it must neither be logged nor reported as a dropped connection.
          if (subscribedGeneration !== channelGeneration) return;
          if (client.connection.state === "closing" || client.connection.state === "closed") return;
          reportStatus?.("disconnected", error);
          console.error(`Ably subscription for "${name}" failed:`, error);
        });
      }
      subscribedChannelName = resolvedChannelName;

      installChannelLifecycle(subscribedChannel, channelLifecycleRegistration);
      return channel;
    };

    const publish: AblyPublish = (name, data) => {
      const activeChannel = channel ?? subscribeChannel();
      if (!activeChannel) return Promise.reject(new Error("Cannot publish before an Ably user channel is available."));
      return activeChannel.publish(name, data);
    };

    const start = () => {
      subscribeChannel();
      onStart?.();
    };

    subscribeChannel();

    installLifecycle(
      client,
      start,
      reportStatus,
      releaseChannel,
      createReplacementClient,
      (replacementClient) => {
        client = replacementClient;
      },
    );

    queueMicrotask(() => {
      if (connectionHealth) {
        store.dispatch(registerConnection(connectionHealth) as UnknownAction);
      }

      if (shouldConnectionStartImmediately) {
        void connect(client, start, reportStatus).catch((error) => {
          console.error("Ably connection failed:", error);
        });
      }
    });

    return (next) => (action) => {
      if (isRealtimeThunk(action)) {
        try {
          return action(store.dispatch, store.getState as () => S, publish);
        } catch (error) {
          console.error("Ably thunk execution failed:", error);
          return undefined as unknown as D;
        }
      } else if (isFunction(action)) {
        try {
          action();
        } catch (error) {
          console.error("Non-Ably thunk execution failed:", error);
        }
      }

      return next(action as D);
    };
  };

  return middleware;
};

function installLifecycle(
  client: AblyRealtimeClient,
  onStart?: () => void,
  reportStatus?: StatusReporter,
  releaseChannel?: () => void,
  createReplacementClient?: () => AblyRealtimeClient,
  replaceClient?: (client: AblyRealtimeClient) => void,
) {
  const registrations = lifecycleRegistrations.get(client) ?? new Set<LifecycleRegistration>();
  registrations.add({ createReplacementClient, onStart, reportStatus, releaseChannel, replaceClient });
  lifecycleRegistrations.set(client, registrations);

  installClientLifecycle(client);
}

function installClientLifecycle(client: AblyRealtimeClient) {
  if (!lifecycleInstalled.has(client)) {
    lifecycleInstalled.add(client);
    client.connection.on((stateChange) => {
      if (stateChange.current === "disconnected") {
        console.info("Ably connection dropped. The SDK is reconnecting.", stateChange.reason);
      }
      if (stateChange.current === "suspended" || stateChange.current === "failed") {
        console.info("Ably connection could not recover.", stateChange.reason);
      }
      reportLifecycleStatus(client, mapConnectionStateToHealth(stateChange.current), stateChange.reason);
    });
  }

  if (lifecycleDocumentHandlers.has(client)) return;

  const applySessionChange: EventListener = () => {
    if (!lifecycleRegistrations.has(client)) return;
    void reconnectForSessionChange(client, () => startLifecycleRegistrations(client), (status, error) =>
      reportLifecycleStatus(client, status, error),
    );
  };

  const handleLoggedOut: EventListener = () => {
    if (!lifecycleRegistrations.has(client)) return;
    reportLifecycleStatus(client, "stopped");
    lastStartedIdentityByClient.delete(client);
    releaseLifecycleChannels(client);
    client.close();
  };

  lifecycleDocumentHandlers.set(client, { applySessionChange, handleLoggedOut });
  document?.addEventListener(SESSION_ESTABLISHED_EVENT, applySessionChange);
  document?.addEventListener(SESSION_AUTH_TOKEN_CHANGED_EVENT, applySessionChange);
  document?.addEventListener("loggedOut", handleLoggedOut);
}

function removeClientLifecycleDocumentHandlers(client: AblyRealtimeClient) {
  const handlers = lifecycleDocumentHandlers.get(client);
  if (!handlers) return;

  document?.removeEventListener(SESSION_ESTABLISHED_EVENT, handlers.applySessionChange);
  document?.removeEventListener(SESSION_AUTH_TOKEN_CHANGED_EVENT, handlers.applySessionChange);
  document?.removeEventListener("loggedOut", handlers.handleLoggedOut);
  lifecycleDocumentHandlers.delete(client);
}

function replaceLifecycleClient(client: AblyRealtimeClient): AblyRealtimeClient | undefined {
  const registrations = lifecycleRegistrations.get(client);
  const createReplacementClient = Array.from(registrations ?? []).find(
    (registration) => registration.createReplacementClient,
  )?.createReplacementClient;
  if (!registrations || !createReplacementClient) return undefined;

  const replacementClient = createReplacementClient();
  removeClientLifecycleDocumentHandlers(client);
  lifecycleRegistrations.delete(client);
  lifecycleRegistrations.set(replacementClient, registrations);
  for (const registration of registrations) {
    registration.replaceClient?.(replacementClient);
  }
  installClientLifecycle(replacementClient);
  return replacementClient;
}

function startLifecycleRegistrations(client: AblyRealtimeClient) {
  for (const registration of lifecycleRegistrations.get(client) ?? []) {
    registration.onStart?.();
  }
}

function releaseLifecycleChannels(client: AblyRealtimeClient) {
  for (const registration of lifecycleRegistrations.get(client) ?? []) {
    registration.releaseChannel?.();
  }
}

function reportLifecycleStatus(client: AblyRealtimeClient, status: ConnectionHealthStatus, error?: unknown) {
  for (const registration of lifecycleRegistrations.get(client) ?? []) {
    registration.reportStatus?.(status, error);
  }
}

function installChannelLifecycle(
  channel: AblyRealtimeChannel,
  registration: ChannelLifecycleRegistration,
) {
  const registrations = channelLifecycleRegistrations.get(channel) ?? new Set<ChannelLifecycleRegistration>();
  registrations.add(registration);
  channelLifecycleRegistrations.set(channel, registrations);
  if (channelLifecycleInstalled.has(channel)) return;
  channelLifecycleInstalled.add(channel);
  channel.on((stateChange) => {
    for (const currentRegistration of channelLifecycleRegistrations.get(channel) ?? []) {
      currentRegistration.reportStatus?.(mapChannelStateToHealth(stateChange.current), stateChange.reason);
      if (stateChange.current === "attached" && stateChange.resumed === false) {
        currentRegistration.onContinuityLost?.();
      }
    }
  });
}

/**
 * Stops a channel from reporting into this middleware's connection health. A channel that belonged
 * to a previous session keeps emitting state changes — including the failure Ably raises once the
 * new session's token no longer grants access to it — and those must not surface as the current
 * session being disconnected.
 */
function releaseChannelLifecycle(channel: AblyRealtimeChannel, registration: ChannelLifecycleRegistration) {
  channelLifecycleRegistrations.get(channel)?.delete(registration);
}

/** Answers whether any middleware other than the one releasing it still listens on this channel. */
function isChannelStillInUse(channel: AblyRealtimeChannel): boolean {
  return (channelLifecycleRegistrations.get(channel)?.size ?? 0) > 0;
}

export default reduxAblyMiddleware;
